import { useCallback, useEffect, useState } from 'react';
import {
  Wallet, Snowflake, AlertTriangle, Bell, TrendingUp, Trash2, ShieldCheck,
  ShieldAlert, ClipboardCheck, ScanLine, Activity, CircleDollarSign, ArrowRight,
  CircleDot, WifiOff, ShoppingCart,
} from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { SIN_PERMISO_COSTO } from '../lib/costo';
import PrimerArranque from '../ui/PrimerArranque';
import NotaExplicativa from '../ui/NotaExplicativa';
import { GraficaABC, GraficaFlujo, GraficaMermas } from '../ui/graficas';
import { clavesDelTablero } from '../ui/tablero';

const pesos = new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
});
const pesosExacto = new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', maximumFractionDigits: 2,
});

// Cómo se presenta cada KPI. `grave` marca los que, cuando no son cero,
// piden acción de alguien — se pintan en rojo para que salten.
const META = {
  valor_inventario_total: { label: 'Valor de inventario', icono: Wallet, dinero: true },
  capital_inmovilizado: { label: 'Capital inmovilizado', icono: Snowflake, dinero: true },
  merma_periodo_valor: { label: 'Mermas del periodo', icono: CircleDollarSign, dinero: true, grave: true },
  merma_periodo_unidades: { label: 'Unidades mermadas', icono: CircleDollarSign, grave: true },
  productos_sin_costo: { label: 'Con stock y costo cero', icono: AlertTriangle, grave: true },
  items_en_cuarentena: { label: 'En cuarentena', icono: Snowflake },
  sku_bajo_minimo: { label: 'Bajo mínimo', icono: AlertTriangle },
  solicitudes_borrado_pendientes: { label: 'Solicitudes de borrado', icono: Trash2 },
  notificaciones_no_leidas: { label: 'Notificaciones', icono: Bell },
  // T-08/T-09 · «Salidas de almacén» y «Ventas» son dos números que no se
  // mezclan: uno cuenta la mercancía que dejó el almacén (SALIDA), el otro
  // lo que se vendió (VENTA). En una empresa de mostrador «Salidas» a secas
  // daba cero para siempre porque el mostrador registra ventas, no salidas.
  salidas_ultimos_30_dias: { label: 'Salidas de almacén (30 días)', icono: TrendingUp },
  ventas_ultimos_30_dias: { label: 'Ventas (30 días)', icono: ShoppingCart },
  aprobaciones_pendientes: { label: 'Esperando autorización', icono: ShieldCheck, grave: true },
  alertas_sin_revisar: { label: 'Movimientos raros sin revisar', icono: ShieldAlert, grave: true },
  conteos_abiertos: { label: 'Conteos en curso', icono: ClipboardCheck },
  discrepancias_sin_ajustar: { label: 'Discrepancias sin corregir', icono: ClipboardCheck, grave: true },
  movimientos_24h: { label: 'Movimientos (24 h)', icono: Activity },
  // TC-29. «Mudas» y no «desconectadas»: una terminal apagada a
  // propósito y una que se cayó se ven igual desde aquí, y la palabra
  // tiene que caber en las dos sin mentir en ninguna.
  terminales: { label: 'Terminales de conteo', icono: CircleDot },
  terminales_mudas: { label: 'Terminales mudas (1 h)', icono: WifiOff, grave: true },
  mis_solicitudes_pendientes: { label: 'Mis solicitudes en espera', icono: ShieldCheck },
  renglones_por_contar: { label: 'Renglones por contar', icono: ClipboardCheck },
};

export default function Tablero({ irA }) {
  const { usuario, tieneNivel, puede } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState(null);
  const [error, setError] = useState(null);
  // EL PRIMER ARRANQUE · criterio 4.2. Solo lo pide quien puede hacer los
  // pasos; para el resto la ruta contesta 403 y aquí no se pinta nada.
  // Un fallo al pedirlo NO rompe el tablero: es un añadido, y un tablero
  // en blanco por un asistente sería el peor cambio posible.
  const [arranque, setArranque] = useState(null);
  const ocultarArranque = useCallback(() => {
    api.post('/arranque/ocultar', {})
      .then(() => setArranque((a) => ({ ...a, oculto: true, mostrar: false })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let vigente = true;
    if (tieneNivel('ADMIN')) {
      api.get('/arranque')
        .then((d) => vigente && setArranque(d))
        .catch(() => {});
    }
    api.get('/dashboard/kpis')
      .then((d) => vigente && setKpis(d))
      .catch((e) => vigente && setError(e.message));
    if (tieneNivel('ENCARGADO')) {
      api.get('/dashboard/series')
        .then((d) => vigente && setSeries(d))
        .catch(() => vigente && setSeries({ flujo: [], abc: [], mermas_por_mes: [] }));
    }
    return () => { vigente = false; };
  }, [tieneNivel]);

  const primerNombre = (usuario?.nombre || '').split(' ')[0] || '';

  if (error) {
    return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>;
  }
  if (!kpis) return <p className="text-sm text-gray-500">Cargando el tablero…</p>;

  const grupos = [kpis.tareas, kpis.operativo, kpis.financiero].filter(Boolean);
  const valores = Object.assign({}, ...grupos);
  const claves = clavesDelTablero(valores);
  const top = kpis.financiero?.top_por_valor || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Hola{primerNombre ? `, ${primerNombre}` : ''}</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          El estado de tu inventario ahora mismo. Cada tarjeta abre la lista de la que sale su número.
        </p>
      </div>

      <PrimerArranque
        estado={arranque}
        irA={irA}
        onOcultar={ocultarArranque}
      />

      <NotaExplicativa titulo="Cómo leer este tablero">
        <strong>Toca cualquier tarjeta</strong> y llegas a la lista exacta que la sustenta, ya
        filtrada. Un cero es un cero medido, no un dato que falte. Lo que está en rojo pide acción
        de alguien.
      </NotaExplicativa>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {claves.map((clave) => (
          <Tarjeta
            key={clave}
            clave={clave}
            valor={valores[clave]}
            destino={kpis.destinos?.[clave]}
            irA={irA}
          />
        ))}
      </div>

      {series && (
        <div className="grid gap-4 xl:grid-cols-2">
          <div className="xl:col-span-2">
            <GraficaFlujo datos={series.flujo} />
          </div>
          {/* SEG-4: las dos van en dinero a costo; sin el permiso el
              servidor las manda en null, y una gráfica de ceros diría
              «no hay valor», que es falso. */}
          {puede?.('costo') ? (
            <>
              <GraficaABC datos={series.abc} formatoValor={(v) => pesos.format(v)} />
              <GraficaMermas datos={series.mermas_por_mes} formatoValor={(v) => pesos.format(v)} />
            </>
          ) : (
            <p className="xl:col-span-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl p-4">
              {SIN_PERMISO_COSTO}
            </p>
          )}
        </div>
      )}

      {top.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-900">Dónde está tu dinero</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Los diez productos que más valor guardan. Son los primeros que hay que contar.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase border-b border-gray-200">
                  <th className="px-4 py-2">Producto</th>
                  <th className="px-4 py-2">Clase</th>
                  <th className="px-4 py-2 text-right">Unidades</th>
                  <th className="px-4 py-2 text-right">Valor</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {top.map((p) => (
                  <tr key={p.sku} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <span className="block font-medium text-gray-900">{p.nombre}</span>
                      <span className="block text-xs font-mono text-gray-500">{p.sku}</span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{p.clase_abc || '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700">{Number(p.unidades)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900">
                      {pesosExacto.format(p.valor)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => irA?.('productos', { sku: p.sku })}
                              className="text-sm font-semibold text-[var(--color-marca)]">
                        Ficha
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="text-xs font-bold text-gray-500 uppercase mb-3">Acciones frecuentes</div>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'escanear', etiqueta: 'Escanear', icono: ScanLine, min: 'CONSULTA' },
            { id: 'conteos', etiqueta: 'Contar un pasillo', icono: ClipboardCheck, min: 'OPERADOR' },
            { id: 'existencias', etiqueta: 'Ver existencias', icono: Activity, min: 'CONSULTA' },
            { id: 'mermas', etiqueta: 'Reporte de mermas', icono: CircleDollarSign, min: 'ENCARGADO' },
          ].filter((a) => tieneNivel(a.min)).map((a) => (
            <button
              key={a.id}
              onClick={() => irA?.(a.id)}
              className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50"
            >
              <a.icono className="h-4 w-4" /> {a.etiqueta}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Tarjeta({ clave, valor, destino, irA }) {
  const meta = META[clave] || { label: clave, icono: Activity };
  const Icono = meta.icono;
  const numero = Number(valor) || 0;
  const alarma = meta.grave && numero > 0;
  const navegable = Boolean(destino && irA);

  const contenido = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-semibold text-gray-500 uppercase leading-tight">
          {meta.label}
        </span>
        <Icono className={`h-4 w-4 shrink-0 ${alarma ? 'text-red-600' : 'text-gray-500'}`} />
      </div>
      <div className={`text-2xl font-semibold mt-1 ${alarma ? 'text-red-700' : 'text-gray-900'}`}>
        {meta.dinero ? pesos.format(numero) : numero}
      </div>
      {navegable && (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-marca)] mt-1">
          Ver detalle <ArrowRight className="h-3 w-3" />
        </span>
      )}
    </>
  );

  if (!navegable) {
    return <div className="bg-white border border-gray-200 rounded-xl p-3">{contenido}</div>;
  }

  return (
    <button
      onClick={() => irA(destino.modulo, destino.contexto)}
      className={`text-left bg-white border rounded-xl p-3 hover:border-[var(--color-marca)] transition-colors ${
        alarma ? 'border-red-200' : 'border-gray-200'
      }`}
    >
      {contenido}
    </button>
  );
}
